/**
 * OSINT confidence helper.
 *
 * По образцу prism (ContactsSection.tsx:1144) confidence — это
 * строковая метка уверенности по результатам OSINT-обогащения
 * конкретного контакта. Поскольку наш провайдер не возвращает
 * это поле напрямую, вычисляем его из числа найденных источников:
 *
 * - HIGH      — найдены источники минимум из 2 разных категорий
 *              (caller-id / мессенджер / соцсеть / онлайн-сервис)
 *              ИЛИ суммарно ≥ 3 источников
 * - MEDIUM    — ровно одна категория с 1-2 источниками
 * - LOW       — единственный найденный источник без подтверждений
 * - UNDEFINED — данных пока нет или обогащение ещё идёт
 */

import type { OsintLookup } from "@/types/osint";

export type OsintConfidence = "HIGH" | "MEDIUM" | "LOW" | "UNDEFINED";

interface ConfidenceBreakdown {
  callerId: number;
  messengers: number;
  socialNetworks: number;
  onlineServices: number;
  total: number;
  categoriesWithData: number;
}

const breakdown = (lookup: OsintLookup | null | undefined): ConfidenceBreakdown => {
  if (!lookup) {
    return {
      callerId: 0,
      messengers: 0,
      socialNetworks: 0,
      onlineServices: 0,
      total: 0,
      categoriesWithData: 0,
    };
  }

  const callerId = Object.keys(lookup.caller_id_services || {}).length;
  const messengers = Object.keys(lookup.messengers || {}).length;
  const socialNetworks = Object.values(lookup.social_networks || {}).reduce(
    (sum, profiles) => sum + (Array.isArray(profiles) ? profiles.length : 0),
    0,
  );
  const onlineServices = Object.keys(lookup.online_services || {}).length;

  const total = callerId + messengers + socialNetworks + onlineServices;
  const categoriesWithData = [callerId, messengers, socialNetworks, onlineServices].filter((n) => n > 0).length;

  return { callerId, messengers, socialNetworks, onlineServices, total, categoriesWithData };
};

/**
 * Вычислить уровень confidence для контакта по его OSINT-данным.
 */
export const computeOsintConfidence = (lookup: OsintLookup | null | undefined): OsintConfidence => {
  const b = breakdown(lookup);
  if (b.total === 0) return "UNDEFINED";
  if (b.categoriesWithData >= 2 || b.total >= 3) return "HIGH";
  if (b.total >= 2) return "MEDIUM";
  return "LOW";
};

/**
 * Tooltip-описание для уровня confidence — показывается в title иконки.
 */
export const describeOsintConfidence = (level: OsintConfidence, lookup: OsintLookup | null | undefined): string => {
  const b = breakdown(lookup);
  const sources = `${b.total} ${b.total === 1 ? "источник" : b.total < 5 ? "источника" : "источников"}`;
  switch (level) {
    case "HIGH":
      return `Высокая уверенность · ${sources} из ${b.categoriesWithData} категорий`;
    case "MEDIUM":
      return `Средняя уверенность · ${sources}`;
    case "LOW":
      return `Низкая уверенность · ${sources}`;
    default:
      return "Уверенность не определена · нет OSINT-данных";
  }
};
